import type { ApiError } from "./types.ts";

/** Thrown for any non-2xx API response, carrying the server's { code, message }. */
export class ApiRequestError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
  ) {
    super(message);
    this.name = "ApiRequestError";
  }
}

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
): Promise<T> {
  const res = await fetch(`/api${path}`, {
    method,
    headers: body ? { "Content-Type": "application/json" } : undefined,
    body: body ? JSON.stringify(body) : undefined,
  });

  if (res.status === 204) return undefined as T;

  // Only parse JSON bodies. Platform errors (e.g. SWA's 401/403 HTML pages) don't
  // return JSON; parsing those would throw a misleading SyntaxError and mask the
  // real status, so guard on content-type and let the !res.ok branch surface the
  // status (a 401 then drives the login redirect in useMe).
  const text = await res.text();
  const isJson = (res.headers.get("content-type") ?? "").includes(
    "application/json",
  );
  const parsed = isJson && text ? (JSON.parse(text) as unknown) : undefined;

  if (!res.ok) {
    const err = (parsed as ApiError | undefined)?.error;
    throw new ApiRequestError(
      res.status,
      err?.code ?? "unknown",
      err?.message ?? res.statusText,
    );
  }
  return parsed as T;
}

export const api = {
  get: <T>(path: string) => request<T>("GET", path),
  post: <T>(path: string, body?: unknown) => request<T>("POST", path, body),
  patch: <T>(path: string, body?: unknown) => request<T>("PATCH", path, body),
  put: <T>(path: string, body?: unknown) => request<T>("PUT", path, body),
  del: <T>(path: string) => request<T>("DELETE", path),
};
